import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Toaster, toast } from "sonner";
import {
  Plane,
  Wrench,
  Globe,
  Shield,
  Award,
  Clock,
  MapPin,
  Mail,
  Phone,
  ChevronDown,
  Menu,
  X,
  Check,
  AlertTriangle,
  Send,
  Anchor,
  Navigation,
  Settings,
  Users,
  Building2,
  Target
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Smooth scroll helper function with retry logic for animated sections
const smoothScrollTo = (targetId) => {
  const scrollToElement = () => {
    const element = document.getElementById(targetId);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const targetPosition = elementPosition - navbarHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
      
      // For sections at the bottom, scroll again after a short delay to account for lazy loading
      setTimeout(() => {
        const newPosition = element.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
        if (Math.abs(newPosition - window.pageYOffset) > 100) {
          window.scrollTo({
            top: newPosition,
            behavior: 'smooth'
          });
        }
      }, 500);
    }
  };
  scrollToElement();
};

// Logo URL
const LOGO_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_b1ebba00-e795-4db2-849a-3fa208c3caac/artifacts/yzokdu88_765f07b4-7ed2-4cc9-9e81-7ab1d6610263.png";

// Image Assets - custom images from client
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/u4cp6pt7_0-3.jpg",
  services: "https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/nw5e8p3d_IMG20230225152207.jpg",
  vip: "https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/xd221k7c_0-18.jpg",
  aircraft: "https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/ym9c7r9b_0-7.jpg",
  cargo: "https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/689l6kqu_0-6.jpg",
  military: "https://images.unsplash.com/photo-1767868280143-c6efe4857e3d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtaWxpdGFyeSUyMHRyYW5zcG9ydCUyMGFpcmNyYWZ0JTIwdGFybWFjfGVufDB8fHx8MTc3Mjg0MDY0M3ww&ixlib=rb-4.1.0&q=85",
  map: "https://images.unsplash.com/photo-1554249906-53d1582266b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHx3b3JsZCUyMG1hcCUyMGRhcmslMjB0ZWNobm9sb2d5JTIwY29ubmVjdGlvbnxlbnwwfHx8fDE3NzI4NDA2Njd8MA&ixlib=rb-4.1.0&q=85"
};

// Navigation Links
const NAV_LINKS = [
  { name: "Home", href: "#home" },
  { name: "About", href: "#about" },
  { name: "Licenses", href: "#licenses" },
  { name: "Aircraft", href: "#aircraft" },
  { name: "Experience", href: "#experience" },
  { name: "Services", href: "#services" },
  { name: "Operations", href: "#operations" },
  { name: "Contact", href: "#contact" },
];

// Aircraft Data
const AIRCRAFT_DATA = {
  boeing: [
    { model: "B747-400", type: "Widebody", engines: "4x CF6/PW4000/RB211" },
    { model: "B747-8", type: "Widebody", engines: "4x GEnx-2B" },
    { model: "B757-200", type: "Narrowbody", engines: "2x RB211/PW2000" },
    { model: "B767-200/300", type: "Widebody", engines: "2x CF6/PW4000" },
    { model: "B777", type: "Widebody", engines: "2x GE90/PW4000/Trent 800" },
    { model: "B737 BBJ", type: "VIP", engines: "2x CFM56" },
  ],
  airbus: [
    { model: "A310-200/300", type: "Widebody", engines: "2x CF6/PW4000" },
    { model: "A318 Elite", type: "VIP", engines: "2x CFM56/PW6000" },
    { model: "A319 ACJ", type: "VIP", engines: "2x CFM56/V2500" },
    { model: "A320 Series", type: "Narrowbody", engines: "2x CFM56/V2500" },
  ],
  other: [
    { model: "MD-11", type: "Widebody", engines: "3x CF6/PW4000" },
  ],
  engines: [
    { model: "V2500", manufacturer: "IAE" },
    { model: "CFM56", manufacturer: "CFM International" },
  ]
};

// Services Data
const SERVICES_DATA = [
  {
    title: "AOG Support",
    description: "24/7 Aircraft on Ground rapid response. Immediate deployment to restore aircraft serviceability and minimize operational disruption.",
    icon: AlertTriangle,
  },
  {
    title: "Line Maintenance",
    description: "Comprehensive line maintenance support including pre-flight checks, transit checks, and routine inspections at any location worldwide.",
    icon: Wrench,
  },
  {
    title: "Aircraft Recovery",
    description: "Expert troubleshooting and recovery operations. Systematic fault isolation and rectification to return aircraft to service.",
    icon: Settings,
  },
  {
    title: "Technical Representative",
    description: "On-site technical representation for airlines, leasing companies, and MRO organizations during aircraft transitions and audits.",
    icon: Users,
  },
  {
    title: "Ferry Flight Support",
    description: "Technical support for ferry flights and aircraft repositioning. Ensuring airworthiness and compliance throughout the operation.",
    icon: Navigation,
  },
  {
    title: "Worldwide Operations",
    description: "Operational aircraft support globally. Short notice deployment capability to any location.",
    icon: Globe,
  },
];

// Experience Data
const EXPERIENCE_DATA = [
  {
    title: "Cargo Operations",
    description: "Extensive experience with major cargo airlines operating B747F, B767F, and MD-11F freighters. Understanding of cargo-specific maintenance requirements and operational pressures. Services & Support for FedEx, World Airways, Kalitta, Atlas Air.",
    icon: Building2,
  },
  {
    title: "VIP Aviation",
    description: "Premium support for corporate and head-of-state aircraft. Discretion, attention to detail, and understanding of VIP operational requirements. Services & Support for Hong Kong Jet, Including VVIP Ops & Presidential flight.",
    icon: Shield,
  },
  {
    title: "Military Operations",
    description: "Background in military aviation environments. Understanding of military procedures, security protocols, and operational requirements.",
    icon: Target,
  },
  {
    title: "Flying Mechanic",
    description: "Flight engineer and flying mechanic experience providing in-flight technical support and immediate maintenance capability at destination.",
    icon: Plane,
  },
  {
    title: "International Deployments",
    description: "Proven track record of successful deployments across all continents. Cultural awareness and adaptability to diverse operational environments.",
    icon: MapPin,
  },
];

// Regions Data
const REGIONS_DATA = [
  { name: "Europe", coords: "50.1°N, 9.0°E" },
  { name: "Middle East", coords: "24.5°N, 54.0°E" },
  { name: "Asia", coords: "35.0°N, 105.0°E" },
  { name: "Africa", coords: "1.0°S, 17.0°E" },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Section Component with animation
const Section = ({ id, children, className = "", dark = true }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  
  return (
    <section
      id={id}
      ref={ref}
      className={`${dark ? "bg-[#050505]" : "bg-[#0A0A0A]"} ${className}`}
    >
      <motion.div
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={staggerContainer}
      >
        {children}
      </motion.div>
    </section>
  );
};

// Navigation Component
const NavigationBar = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass py-3" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3" data-testid="logo-link">
          <span className="font-heading text-white text-lg font-bold tracking-wider">KLEPTONIC VENTURES</span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6 ml-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.name}
              href={link.href}
              data-testid={`nav-link-${link.name.toLowerCase()}`}
              className="nav-link text-sm uppercase tracking-widest text-gray-400 hover:text-[#3B82F6] transition-colors font-medium"
              onClick={(e) => {
                e.preventDefault();
                const targetId = link.href.replace('#', '');
                smoothScrollTo(targetId);
              }}
            >
              {link.name}
            </a>
          ))}
        </div>

        <div className="flex-1"></div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-gray-300 hover:text-[#3B82F6] transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass mt-2 mx-4 rounded-sm overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col py-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className="px-6 py-3 text-sm uppercase tracking-widest text-gray-400 hover:text-[#3B82F6] hover:bg-white/5 transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileMenuOpen(false);
                    const targetId = link.href.replace('#', '');
                    smoothScrollTo(targetId);
                  }}
                >
                  {link.name}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-start"
      style={{
        backgroundImage: `url(${IMAGES.hero})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/30" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-3xl"
        >
          <p className="text-[#3B82F6] font-mono text-sm tracking-widest uppercase mb-4">
            Aircraft Maintenance Engineering
          </p>
          <div className="flex items-center gap-6 mb-6">
            <img src={LOGO_URL} alt="KLEPTONIC VENTURES Aviation" className="h-[400px] w-auto mix-blend-lighten" />
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight leading-tight">
              KLEPTONIC VENTURES<br/>
              <span className="text-[#3B82F6] whitespace-nowrap">Aviation Services</span>
            </h1>
          </div>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8 max-w-xl -mt-8">
            FAA & EASA Part-66 Licensed Aircraft Maintenance Engineering. 
            Widebody expertise. Worldwide operations. Short notice deployment.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="#contact"
              data-testid="hero-contact-btn"
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold uppercase tracking-wider px-6 py-3 rounded-sm text-center text-sm transition-all"
              onClick={(e) => {
                e.preventDefault();
                smoothScrollTo('contact');
              }}
            >
              Contact Us
            </a>
            <a
              href="#services"
              data-testid="hero-services-btn"
              className="border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white font-semibold uppercase tracking-wider px-6 py-3 rounded-sm text-center text-sm transition-all"
              onClick={(e) => {
                e.preventDefault();
                smoothScrollTo('services');
              }}
            >
              Our Services
            </a>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <a href="#about" className="flex flex-col items-center text-gray-500 hover:text-[#3B82F6] transition-colors">
          <span className="text-xs uppercase tracking-widest mb-2">Scroll</span>
          <ChevronDown className="animate-bounce" size={20} />
        </a>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <Section id="about" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div variants={fadeInUp} className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#3B82F6] font-mono text-sm tracking-widest uppercase mb-4">
              About Us
            </p>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase tracking-tight mb-6">
              Engineering Excellence
            </h2>
            <div className="space-y-4 text-gray-400 leading-relaxed">
              <p>
                KLEPTONIC VENTURES LTD provides specialized aircraft maintenance engineering services 
                to airlines, cargo operators, and VIP aviation clients. Our expertise spans 
                widebody aircraft platforms including Boeing 737, 747, 757, 767, 777, MD-11, 
                and Airbus A310-200/300 and A320 series.
              </p>
              <p>
                With dual FAA certification and EASA Part-66 B1/B2 licenses, we offer 
                comprehensive mechanical and avionics support capabilities.
              </p>
              <p>
                Our background also includes operational aviation experience, providing a 
                unique insight into flight operations that enhances our maintenance perspective.
              </p>
              <p>
                This combination of engineering expertise and operational knowledge allows us 
                to understand aircraft systems from both the maintenance and flight crew 
                perspectives, delivering solutions that prioritize airworthiness, safety, 
                and operational efficiency.
              </p>
            </div>
          </div>
          
          <motion.div variants={fadeInUp} className="relative">
            <div className="aspect-[4/3] overflow-hidden rounded-sm">
              <img
                src={IMAGES.cargo}
                alt="Cargo Aircraft Operations"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#0A0A0A] border border-white/10 p-6 max-w-xs">
              <p className="font-mono text-[#3B82F6] text-sm mb-2">CERTIFICATIONS</p>
              <p className="text-white font-heading text-lg">FAA & EASA Part-66 B1/B2</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Section>
  );
};

// Licenses Section
const LicensesSection = () => {
  const licenses = [
    {
      title: "FAA Aircraft Maintenance License",
      authority: "Federal Aviation Administration",
      description: "U.S. certification for aircraft maintenance and repair authorization on FAA-registered aircraft.",
      icon: Shield,
    },
    {
      title: "EASA Part-66 B1/B2",
      authority: "European Union Aviation Safety Agency",
      description: "European certification covering mechanical (B1) and avionic (B2) systems for EASA-registered aircraft.",
      icon: Award,
    },
  ];

  return (
    <Section id="licenses" dark={false} className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <p className="text-[#3B82F6] font-mono text-sm tracking-widest uppercase mb-4">
            Certifications
          </p>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase tracking-tight mb-4">
            Licenses & Qualifications
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Internationally recognized certifications ensuring compliance and competence 
            across regulatory frameworks.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {licenses.map((license, index) => (
            <motion.div
              key={license.title}
              variants={fadeInUp}
              className="group bg-[#050505] border border-white/5 hover:border-[#3B82F6]/50 p-8 transition-all duration-500 card-hover"
              data-testid={`license-card-${index}`}
            >
              <div className="flex items-start gap-6">
                <div className="service-icon flex-shrink-0">
                  <license.icon className="text-[#3B82F6]" size={24} />
                </div>
                <div>
                  <p className="font-mono text-[#3B82F6] text-xs tracking-widest mb-2">
                    {license.authority}
                  </p>
                  <h3 className="font-heading text-xl text-white font-semibold mb-3">
                    {license.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {license.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// Aircraft Experience Section
const AircraftSection = () => {
  const [activeTab, setActiveTab] = useState("boeing");

  return (
    <Section id="aircraft" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Title + Image Side by Side */}
        <motion.div variants={fadeInUp} className="grid lg:grid-cols-2 gap-12 items-start mb-12">
          <div>
            <p className="text-[#3B82F6] font-mono text-sm tracking-widest uppercase mb-4">
              Fleet Experience
            </p>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase tracking-tight mb-6">
              Aircraft Experience
            </h2>
            <div className="space-y-4">
              <p className="text-gray-400 leading-relaxed">
                Our experience spans a wide range of aviation environments, supporting aircraft operations across commercial airlines, cargo operators, and VIP aviation platforms worldwide.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Over the course of a 36-year international career in aviation, we have worked in diverse operational settings including line maintenance, transit and daily checks, heavy maintenance, and hangar inspections, ensuring aircraft reliability and safety under demanding operational conditions.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Extensive experience has been gained supporting widebody cargo operations, as well as VIP aircraft requiring the highest levels of precision, discretion, and reliability.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Our background also includes Flight Engineer operational support, providing direct technical assistance during flight operations and offering valuable insight into aircraft systems from both the engineering and flight crew perspectives.
              </p>
              <p className="text-gray-400 leading-relaxed">
                International assignments have provided significant operational exposure across Asia, Africa, the Middle East, and Europe, working in complex environments where adaptability, rapid troubleshooting, and operational efficiency are critical.
              </p>
              <p className="text-gray-400 leading-relaxed">
                In addition, our experience includes working within military aviation environments, where strict procedures, operational discipline, and technical excellence are essential.
              </p>
              <p className="text-gray-400 leading-relaxed">
                This diverse operational background provides a comprehensive understanding of aircraft systems, operational constraints, and maintenance requirements across multiple aviation sectors worldwide.
              </p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-sm">
            <img
              src={IMAGES.aircraft}
              alt="Boeing 747 Aircraft"
              className="w-full h-auto object-contain"
            />
            <div className="absolute bottom-4 left-4">
              <p className="font-mono text-[#3B82F6] text-sm">WIDEBODY EXPERTISE</p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4 mb-12">
          {["boeing", "airbus", "other", "engines"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`aircraft-tab-${tab}`}
              className={`px-6 py-3 font-heading uppercase tracking-wider text-sm transition-all ${
                activeTab === tab
                  ? "bg-[#3B82F6] text-black font-bold"
                  : "border border-white/10 text-gray-400 hover:border-[#3B82F6]/50 hover:text-white"
              }`}
            >
              {tab === "other" ? "McDonnell Douglas" : tab}
            </button>
          ))}
        </motion.div>

        {/* Aircraft Grid */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          key={activeTab}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {(activeTab === "engines" ? AIRCRAFT_DATA.engines : AIRCRAFT_DATA[activeTab])?.map((item, index) => (
            <motion.div
              key={item.model}
              variants={fadeInUp}
              className="aircraft-card bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#3B82F6]/30 transition-all"
              data-testid={`aircraft-card-${activeTab}-${index}`}
            >
              <div className="flex items-center gap-4 mb-4">
                <Plane className="text-[#3B82F6]" size={20} />
                <h3 className="font-heading text-xl text-white font-semibold">
                  {item.model}
                </h3>
              </div>
              <div className="space-y-2 font-mono text-sm">
                {item.type && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">TYPE</span>
                    <span className="text-gray-300">{item.type}</span>
                  </div>
                )}
                {item.engines && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">ENGINES</span>
                    <span className="text-gray-300">{item.engines}</span>
                  </div>
                )}
                {item.manufacturer && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">MFR</span>
                    <span className="text-gray-300">{item.manufacturer}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

// Operational Experience Section
const ExperienceSection = () => {
  return (
    <Section id="experience" dark={false} className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div variants={fadeInUp} className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-[#3B82F6] font-mono text-sm tracking-widest uppercase mb-4">
              Background
            </p>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase tracking-tight mb-6">
              Operational Experience
            </h2>
            <p className="text-gray-400 leading-relaxed mb-8">
              Proven track record across diverse aviation environments, backed by over 36 years 
              of worldwide operational experience. From cargo freighters operating continuous 
              schedules to head-of-state aircraft requiring absolute discretion, our expertise 
              adapts to the most demanding operational contexts.
            </p>
            <p className="text-gray-400 leading-relaxed mb-8">
              Our experience encompasses military aviation protocols, VIP operations, and 
              international deployments across every continent.
            </p>
            <p className="text-gray-400 leading-relaxed mb-8">
              This extensive global exposure allows us to operate efficiently in complex 
              environments while maintaining the highest standards of safety, reliability, 
              and operational readiness.
            </p>
            
            <div className="relative aspect-video overflow-hidden rounded-sm">
              <img
                src={IMAGES.vip}
                alt="VIP Aircraft"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          </div>

          <div className="space-y-4">
            {EXPERIENCE_DATA.map((exp, index) => (
              <motion.div
                key={exp.title}
                variants={fadeInUp}
                className="bg-[#050505] border border-white/5 p-6 hover:border-[#3B82F6]/30 transition-all card-hover"
                data-testid={`experience-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="service-icon flex-shrink-0 w-12 h-12">
                    <exp.icon className="text-[#3B82F6]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-white font-semibold mb-2">
                      {exp.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {exp.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <Section id="services" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <p className="text-[#3B82F6] font-mono text-sm tracking-widest uppercase mb-4">
            What We Do
          </p>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase tracking-tight mb-4">
            Services
          </h2>
        </motion.div>

        {/* Services Image Banner - 3 photos */}
        <motion.div variants={fadeInUp} className="mb-12">
          <div className="grid grid-cols-3 gap-4">
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/ulayrzkv_1d329757-4b37-4fd4-bf9b-6b0f1420c2f4.png"
                alt="Boeing 737 BBJ"
                className="w-full h-48 object-cover"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/j64vjaxp_244b7f38-a6bd-448f-9754-bbb3f506a679.png"
                alt="Airbus A320neo"
                className="w-full h-48 object-cover"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/idk5df8j_0315ef19-1590-4d71-9922-6c1f94621a36-1.png"
                alt="Boeing 747-800"
                className="w-full h-48 object-cover"
              />
            </div>
          </div>
        </motion.div>

        {/* Introduction */}
        <motion.div variants={fadeInUp} className="mb-16">
          <p className="text-gray-400 leading-relaxed mb-4">
            KLEPTONIC VENTURES Aviation Services provides comprehensive aircraft maintenance engineering support tailored to the demanding operational environments of commercial airlines, cargo operators, and VIP aviation.
          </p>
          <p className="text-gray-400 leading-relaxed">
            Our services are designed to support aircraft operators with reliable, responsive, and highly experienced technical expertise during daily operations, line maintenance activities, and operational troubleshooting.
          </p>
        </motion.div>

        {/* Line Maintenance & Operational Support */}
        <motion.div variants={fadeInUp} className="mb-16">
          <h3 className="font-heading text-xl text-[#3B82F6] font-semibold mb-6 uppercase tracking-wider">
            Line Maintenance & Operational Support
          </h3>
          <p className="text-gray-400 leading-relaxed mb-6">
            We provide hands-on line maintenance engineering services including:
          </p>
          <div className="space-y-4">
            <div className="border-l-2 border-[#3B82F6] pl-6">
              <h4 className="text-white font-semibold mb-2">Transit Checks</h4>
              <p className="text-gray-400 text-sm leading-relaxed">Rapid inspection and servicing between flights to ensure aircraft airworthiness and operational readiness within tight turnaround schedules.</p>
            </div>
            <div className="border-l-2 border-[#3B82F6] pl-6">
              <h4 className="text-white font-semibold mb-2">Daily Checks</h4>
              <p className="text-gray-400 text-sm leading-relaxed">Scheduled daily inspections verifying aircraft systems, fluid levels, structural condition, and overall serviceability.</p>
            </div>
            <div className="border-l-2 border-[#3B82F6] pl-6">
              <h4 className="text-white font-semibold mb-2">Servicing Operations</h4>
              <p className="text-gray-400 text-sm leading-relaxed">Hydraulic, oil, oxygen servicing, tire pressure checks, and other essential aircraft servicing required to maintain operational performance.</p>
            </div>
            <div className="border-l-2 border-[#3B82F6] pl-6">
              <h4 className="text-white font-semibold mb-2">Defect Rectification & Troubleshooting</h4>
              <p className="text-gray-400 text-sm leading-relaxed">Advanced diagnostic and troubleshooting support for mechanical and avionic systems to quickly identify and resolve technical issues, minimizing operational downtime.</p>
            </div>
          </div>
        </motion.div>

        {/* Technical Troubleshooting & AOG Support */}
        <motion.div variants={fadeInUp} className="mb-16">
          <h3 className="font-heading text-xl text-[#3B82F6] font-semibold mb-6 uppercase tracking-wider">
            Technical Troubleshooting & AOG Support
          </h3>
          <p className="text-gray-400 leading-relaxed mb-4">
            With extensive experience across multiple aircraft platforms, we provide high-level troubleshooting assistance during technical events, operational disruptions, or Aircraft On Ground (AOG) situations.
          </p>
          <p className="text-gray-400 leading-relaxed">
            Our expertise allows rapid fault isolation and system analysis, ensuring efficient corrective actions while maintaining full compliance with aviation regulatory standards.
          </p>
        </motion.div>

        {/* Flight Mechanic / Flight Engineer */}
        <motion.div variants={fadeInUp} className="mb-16">
          <h3 className="font-heading text-xl text-[#3B82F6] font-semibold mb-6 uppercase tracking-wider">
            Flight Mechanic / Flight Engineer Operational Support
          </h3>
          <p className="text-gray-400 leading-relaxed mb-4">
            In addition to maintenance engineering services, we bring operational experience as Flight Engineer / Flight Mechanic support on widebody aircraft such as the Boeing 747 and MD-11 platforms.
          </p>
          <p className="text-gray-400 leading-relaxed">
            This unique capability allows us to bridge the gap between flight operations and aircraft maintenance, providing deeper system understanding, improved troubleshooting capability, and enhanced communication between engineering and flight crews.
          </p>
        </motion.div>

        {/* Specialized Operations Experience */}
        <motion.div variants={fadeInUp} className="mb-16">
          <h3 className="font-heading text-xl text-[#3B82F6] font-semibold mb-6 uppercase tracking-wider">
            Specialized Operations Experience
          </h3>
          <p className="text-gray-400 leading-relaxed mb-6">
            Our operational background includes support in complex environments such as:
          </p>
          <div className="flex flex-wrap gap-4">
            <span className="px-6 py-3 border border-white/10 text-gray-400 text-sm uppercase tracking-wider">
              VIP Aircraft Operations
            </span>
            <span className="px-6 py-3 border border-white/10 text-gray-400 text-sm uppercase tracking-wider">
              Worldwide Cargo Operations
            </span>
            <span className="px-6 py-3 border border-white/10 text-gray-400 text-sm uppercase tracking-wider">
              Remote and International Deployments
            </span>
          </div>
          <p className="text-gray-400 leading-relaxed mt-6">
            This experience ensures discretion, adaptability, and reliability in demanding aviation environments.
          </p>
        </motion.div>

        {/* Target Clients */}
        <motion.div variants={fadeInUp} className="mt-20">
          <h3 className="font-heading text-2xl text-white font-semibold mb-8 text-center">
            Target Clients
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              "Cargo Airlines",
              "VIP Operators",
              "Leasing Companies",
              "MRO Organizations"
            ].map((client) => (
              <span
                key={client}
                className="px-6 py-3 border border-white/10 text-gray-400 text-sm uppercase tracking-wider hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors"
              >
                {client}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Portfolio Gallery */}
        <motion.div variants={fadeInUp} className="mt-20">
          <h3 className="font-heading text-xl text-[#3B82F6] font-semibold mb-8 text-center uppercase tracking-wider">
            Operations Portfolio
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="overflow-hidden rounded-sm">
              <img
                src={IMAGES.services}
                alt="Aircraft Maintenance"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/wn10ubml_0-15.jpg"
                alt="Operations 1"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/lvmpubxp_0-14.jpg"
                alt="Operations 2"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/wwgz9ugb_0-16.jpg"
                alt="Operations 3"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/qlo302p8_6ace4ed8-cdc7-4740-b847-aa1c4d8abc57.jpg"
                alt="FMC Display"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/ag5tbfxj_2d64a929-3c34-4149-aeed-654d1015c754.jpg"
                alt="Atlas Air Aircraft"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/71gvt5fi_j2o22og5rvg31.webp"
                alt="Boeing 747 Cockpit"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/qh9zvujc_0-22.jpg"
                alt="Aircraft Operations"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/84w5jno7_0-20.jpg"
                alt="Flight Operations"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_flight-engineer-pro/artifacts/rr6c49n9_IMG_4714.jpg"
                alt="Atlas Air Tail Maintenance"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

// Operations Section (Global Coverage)
const OperationsSection = () => {
  return (
    <Section id="operations" dark={false} className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div variants={fadeInUp} className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#3B82F6] font-mono text-sm tracking-widest uppercase mb-4">
              Global Reach
            </p>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase tracking-tight mb-6">
              Operational Areas
            </h2>
            <p className="text-gray-400 leading-relaxed mb-8">
              Worldwide deployment capability with proven experience across all major 
              aviation regions. Short notice response available for AOG situations and 
              critical maintenance requirements.
            </p>

            <div className="space-y-4">
              {REGIONS_DATA.map((region) => (
                <div
                  key={region.name}
                  className="flex items-center justify-between py-4 border-b border-white/5"
                  data-testid={`region-${region.name.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex items-center gap-4">
                    <MapPin className="text-[#3B82F6]" size={18} />
                    <span className="font-heading text-white text-lg">
                      {region.name}
                    </span>
                  </div>
                  <span className="font-mono text-gray-500 text-sm">
                    {region.coords}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-[#050505] border border-[#3B82F6]/30">
              <div className="flex items-center gap-4">
                <Clock className="text-[#3B82F6]" size={24} />
                <div>
                  <p className="font-heading text-white font-semibold">
                    Short Notice Deployment
                  </p>
                  <p className="text-gray-400 text-sm">
                    Available for rapid deployment worldwide
                  </p>
                </div>
              </div>
            </div>
          </div>

          <motion.div variants={fadeInUp} className="relative">
            <div className="aspect-square overflow-hidden rounded-sm">
              <img
                src={IMAGES.map}
                alt="Global Operations"
                className="w-full h-full object-cover opacity-70"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            </div>
            
            {/* Overlay stats */}
            <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-4">
              {[
                { value: "5", label: "Continents" },
                { value: "24/7", label: "Availability" },
                { value: "100+", label: "Deployments" },
              ].map((stat) => (
                <div key={stat.label} className="text-center glass p-4">
                  <p className="font-heading text-2xl text-[#3B82F6] font-bold">
                    {stat.value}
                  </p>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <Section id="contact" className="py-24 lg:py-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <p className="text-[#3B82F6] font-mono text-sm tracking-widest uppercase mb-4">
            Get In Touch
          </p>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase tracking-tight mb-4">
            Contact
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Ready to discuss your aircraft maintenance requirements? Contact us for 
            immediate support or to schedule a consultation.
          </p>
        </motion.div>

        {/* Contact Info */}
        <motion.div variants={fadeInUp}>
          <div className="bg-[#0A0A0A] border border-white/5 p-8">
            <h3 className="font-heading text-xl text-white font-semibold mb-6">
              Contact Information
            </h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <Mail className="text-[#3B82F6] mt-1" size={20} />
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wider mb-1">Email</p>
                  <a href="mailto:kleptonic.venture@proton.me" className="text-white hover:text-[#3B82F6] transition-colors">
                    kleptonic.venture@proton.me
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="text-[#3B82F6] mt-1" size={20} />
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wider mb-1">Phone</p>
                  <a href="tel:+441234567890" className="text-white hover:text-[#3B82F6] transition-colors">
                    Available upon request
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Globe className="text-[#3B82F6] mt-1" size={20} />
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wider mb-1">Operations</p>
                  <p className="text-white">Worldwide</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-[#050505] border-t border-white/5 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="KLEPTONIC VENTURES Aviation" className="h-14 w-auto" />
            <div>
              <p className="font-heading text-white font-semibold">KLEPTONIC VENTURES</p>
              <p className="text-gray-500 text-sm">Aviation Services</p>
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6">
            {NAV_LINKS.slice(0, 6).map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-gray-500 hover:text-[#3B82F6] text-sm uppercase tracking-wider transition-colors"
              >
                {link.name}
              </a>
            ))}
          </div>
          
          <p className="text-gray-600 text-sm">
            &copy; {new Date().getFullYear()} KLEPTONIC VENTURES. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App" data-testid="kleptonic-app">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#E0E0E0",
          },
        }}
      />
      
      <NavigationBar
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      
      <HeroSection />
      <AboutSection />
      <LicensesSection />
      <AircraftSection />
      <ExperienceSection />
      <ServicesSection />
      <OperationsSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
